import { Ban, Wrench, Heart, Lock, HandHeart, Phone, ArrowUpRight, MessageCircle } from "lucide-react";
const NAVY = "#081438";
const NAVY_LIGHT = "#243752";
const GOLD = "#F5A623";
const GOLD_DARK = "#9A6400";
const CREAM = "#FAF7F1";
const CREAM_DEEP = "#F2EDE2";
const PAPER = "#FFFEFB";
const INK = "#1A1F2E";
const INK_MUTED = "#5A6478";
const FONT_HEAD = "'Plus Jakarta Sans', system-ui, sans-serif";
const FONT_BODY = "'Manrope', system-ui, sans-serif";
type Pledge = {
  icon: React.ComponentType<{
    className?: string;
    strokeWidth?: number;
  }>;
  title: string;
  body: string;
};
const PLEDGES: Pledge[] = [{
  icon: Ban,
  title: "No callout fee, ever",
  body: "We don't charge to come look at your opener or diagnose the fault. If you don't approve the quote, you walk away at zero cost."
}, {
  icon: Wrench,
  title: "Repair first. Replace only when honest.",
  body: "Almost always there's a way to fix what you have — a gear, a capacitor, a sensor. We tell you that first. Replacement is the last option, not the default."
}, {
  icon: HandHeart,
  title: "We work with your budget",
  body: "Tell us what you can spend. We'll lay out the options — full repair, partial fix, like-for-like swap, or upgrade — and you decide what makes sense."
}, {
  icon: Lock,
  title: "Fixed quote, in writing, before any tool",
  body: "Once we diagnose on-site, the price is locked. The number on the quote is the same number on the invoice. No hourly creep, no surprise extras."
}, {
  icon: Heart,
  title: "Walk-away-free guarantee",
  body: "Don't like the quote? No pressure, no scripts, no callout charge. We shake hands and leave. Your home, your call, every time."
}];
export default function MotorPricing() {
  return <section className="relative overflow-hidden" style={{
    backgroundColor: CREAM,
    fontFamily: FONT_BODY
  }} aria-labelledby="motor-pricing-heading">
      {/* Paper grain */}
      <div aria-hidden className="absolute inset-0 opacity-[0.05] pointer-events-none mix-blend-multiply" style={{
      backgroundImage: "url(\"data:image/svg+xml;utf8,<svg viewBox='0 0 200 200' xmlns='http://www.w3.org/2000/svg'><filter id='n'><feTurbulence type='fractalNoise' baseFrequency='0.9' numOctaves='2' stitchTiles='stitch'/><feColorMatrix values='0 0 0 0 0.05 0 0 0 0 0.08 0 0 0 0 0.22 0 0 0 0.6 0'/></filter><rect width='100%' height='100%' filter='url(%23n)'/></svg>\")"
    }} />

      {/* Decorative gold blob */}
      <div aria-hidden className="absolute -bottom-32 -left-32 w-[500px] h-[500px] rounded-full pointer-events-none opacity-[0.05]" style={{
      backgroundColor: GOLD,
      filter: "blur(80px)"
    }} />

      <div className="relative mx-auto max-w-7xl px-6 lg:px-10 pt-24 md:pt-32 pb-20 md:pb-28">

        {/* Top strip */}
        <div className="flex items-center justify-between gap-6 mb-12 md:mb-14 flex-wrap">
          <div className="flex items-center gap-3">
            <span className="inline-flex items-center justify-center w-7 h-7 rounded-full border" style={{
            borderColor: `${GOLD_DARK}66`,
            backgroundColor: `${GOLD}1A`
          }}>
              <HandHeart className="h-3.5 w-3.5" strokeWidth={2.5} style={{
              color: GOLD_DARK
            } as React.CSSProperties} />
            </span>
            <span className="text-[11px] font-bold tracking-[0.28em] uppercase" style={{
            color: GOLD_DARK
          }}>
              Fair Pricing · Honest Quotes
            </span>
            <span className="hidden md:block h-px w-12" style={{
            backgroundColor: `${NAVY}26`
          }} />
          </div>
          <div className="text-[10px] font-semibold tracking-[0.22em] uppercase" style={{
          color: INK_MUTED
        }}>
            5 Pledges · One Family · Zero Pressure
          </div>
        </div>

        {/* Headline */}
        <div className="mb-14 md:mb-20 grid grid-cols-1 lg:grid-cols-12 gap-6 items-end">
          <h2 id="motor-pricing-heading" className="lg:col-span-9 font-extrabold" style={{
          fontFamily: FONT_HEAD,
          color: NAVY,
          fontSize: "clamp(2rem, 5vw, 4rem)",
          lineHeight: 1.02,
          letterSpacing: "-0.03em"
        }}>
            Fair pricing isn&rsquo;t a marketing line.
            <br className="hidden md:block" />
            It&rsquo;s how we&rsquo;ve{" "}
            <span style={{
            color: GOLD_DARK
          }}>stayed in business</span> since 2009.
          </h2>
          <p className="lg:col-span-3 text-[14px] leading-relaxed" style={{
          color: INK_MUTED
        }}>
            Five pledges we make on every motor job &mdash; before any tool comes
            out, before any number is locked in.
          </p>
        </div>

        {/* Asymmetric grid */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-12 lg:gap-16">

          {/* LEFT — 5 Pledges */}
          <div className="lg:col-span-6">
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-7" style={{
            color: INK_MUTED
          }}>
              &mdash; Our pricing pledges
            </div>

            <ul className="space-y-6">
              {PLEDGES.map((pledge, idx) => <PledgeRow key={pledge.title} pledge={pledge} num={String(idx + 1).padStart(2, "0")} />)}
            </ul>
          </div>

          {/* RIGHT — Honest letter */}
          <div className="lg:col-span-6 lg:sticky lg:top-32 lg:self-start">
            <div className="text-[10px] font-bold tracking-[0.32em] uppercase mb-7" style={{
            color: INK_MUTED
          }}>
              &mdash; An honest note from us
            </div>

            <article className="relative rounded-2xl border overflow-hidden" style={{
            backgroundColor: PAPER,
            borderColor: `${NAVY}1A`,
            boxShadow: `0 20px 60px -20px ${NAVY}26`
          }}>
              {/* Top gold accent */}
              <div aria-hidden className="absolute top-0 left-8 right-8 h-px" style={{
              background: `linear-gradient(90deg, ${GOLD} 0%, ${GOLD}40 100%)`
            }} />

              <div className="p-6 md:p-8">
                {/* Letterhead */}
                <div className="flex items-center gap-3 mb-6 pb-5 border-b" style={{
                borderColor: `${NAVY}14`
              }}>
                  <div className="flex-shrink-0 flex items-center justify-center w-11 h-11 rounded-full font-extrabold text-sm" style={{
                  backgroundColor: NAVY,
                  color: GOLD,
                  fontFamily: FONT_HEAD,
                  letterSpacing: "-0.02em"
                }}>
                    GG
                  </div>
                  <div>
                    <div className="text-[10px] font-bold tracking-[0.22em] uppercase mb-0.5" style={{
                    color: GOLD_DARK
                  }}>
                      From the family
                    </div>
                    <div className="text-[13px] font-bold" style={{
                    color: NAVY,
                    fontFamily: FONT_HEAD,
                    letterSpacing: "-0.01em"
                  }}>
                      Gold Garage Door · Bass Hill NSW
                    </div>
                  </div>
                </div>

                {/* Letter title */}
                <h3 className="font-extrabold mb-5" style={{
                fontFamily: FONT_HEAD,
                color: NAVY,
                fontSize: "clamp(1.375rem, 2.4vw, 1.75rem)",
                lineHeight: 1.2,
                letterSpacing: "-0.02em"
              }}>
                  Why we don&rsquo;t list motor prices online.
                </h3>

                {/* Letter body */}
                <div className="space-y-4">
                  <p className="text-[15px] leading-[1.7]" style={{
                  color: INK
                }}>
                    We could throw{" "}
                    <span className="line-through font-semibold" style={{
                    color: INK_MUTED
                  }}>
                      &ldquo;from $99&rdquo;
                    </span>{" "}
                    on this page like everyone else. We don&rsquo;t &mdash;
                    because it wouldn&rsquo;t be honest.
                  </p>

                  <p className="text-[14.5px] leading-[1.7]" style={{
                  color: INK_MUTED
                }}>
                    A Forza 1200 control-board failure isn&rsquo;t the same job
                    as a Merlin gear strip, which isn&rsquo;t the same as a B&amp;D
                    capacitor swap. Brand, age, parts availability, and what
                    actually failed all change the price. Posting a fake
                    &ldquo;from&rdquo; number would just hide the truth.
                  </p>

                  <p className="text-[14.5px] leading-[1.7]" style={{
                  color: INK_MUTED
                }}>
                    What we{" "}
                    <em style={{
                    color: NAVY,
                    fontWeight: 600
                  }}>can</em>{" "}
                    promise: no callout fee for the inspection, no obligation if
                    you don&rsquo;t like the quote, no upsell scripts, and no
                    technicians earning commission on parts.{" "}
                    <span style={{
                    color: NAVY,
                    fontWeight: 600
                  }}>
                      Tell us what you can spend &mdash; we&rsquo;ll work with
                      you.
                    </span>
                  </p>
                </div>

                {/* Signature */}
                <div className="mt-7 pt-6 border-t flex items-center justify-between gap-4 flex-wrap" style={{
                borderColor: `${NAVY}14`
              }}>
                  <div>
                    <p className="text-[14px] font-bold italic" style={{
                    color: NAVY,
                    fontFamily: FONT_HEAD
                  }}>
                      &mdash; The Gold Family
                    </p>
                    <p className="text-[10px] font-semibold tracking-[0.18em] uppercase mt-1" style={{
                    color: INK_MUTED
                  }}>
                      Family-run since 2009
                    </p>
                  </div>

                  {/* Stamp */}
                  <div aria-hidden className="border-2 rounded-md px-3 py-1.5 text-center" style={{
                  borderColor: `${GOLD_DARK}99`,
                  backgroundColor: `${GOLD}0F`,
                  transform: "rotate(-3deg)"
                }}>
                    <div className="text-[8px] font-extrabold tracking-[0.22em] uppercase" style={{
                    color: GOLD_DARK,
                    fontFamily: FONT_HEAD
                  }}>
                      Honest
                    </div>
                    <div className="text-[11px] font-extrabold tracking-[0.16em] uppercase" style={{
                    color: GOLD_DARK,
                    fontFamily: FONT_HEAD
                  }}>
                      Quote
                    </div>
                  </div>
                </div>
              </div>
            </article>

            {/* Twin CTAs below letter */}
            <div className="mt-6 grid grid-cols-2 gap-3">
              <a href="tel:0485000933" className="group inline-flex items-center justify-center gap-2 rounded-xl px-4 py-3.5 text-[13px] font-bold transition-all duration-200 hover:scale-[1.02] active:scale-[0.98]" style={{
              backgroundColor: NAVY,
              color: "white",
              fontFamily: FONT_BODY,
              boxShadow: `0 12px 32px -10px ${NAVY}66`
            }}>
                <Phone className="h-4 w-4" strokeWidth={2.5} />
                Call now
                <ArrowUpRight className="h-3.5 w-3.5 transition-transform group-hover:translate-x-0.5 group-hover:-translate-y-0.5" strokeWidth={2.5} />
              </a>

              <a href="https://wa.me/61485000933" className="group inline-flex items-center justify-center gap-2 rounded-xl border-2 px-4 py-3 text-[13px] font-bold transition-all duration-200" style={{
              borderColor: GOLD_DARK,
              color: NAVY,
              fontFamily: FONT_BODY
            }} onMouseEnter={e => {
              e.currentTarget.style.backgroundColor = `${GOLD}1A`;
            }} onMouseLeave={e => {
              e.currentTarget.style.backgroundColor = "transparent";
            }}>
                <MessageCircle className="h-4 w-4" strokeWidth={2.5} />
                WhatsApp
              </a>
            </div>
          </div>
        </div>
      </div>
    </section>;
}
function PledgeRow({
  pledge,
  num
}: {
  pledge: Pledge;
  num: string;
}) {
  const Icon = pledge.icon;
  return <li className="group flex items-start gap-4">
      {/* Icon + number */}
      <div className="flex-shrink-0 flex flex-col items-center gap-1">
        <div className="flex items-center justify-center w-11 h-11 rounded-xl border transition-all duration-300 group-hover:scale-105" style={{
        backgroundColor: PAPER,
        borderColor: `${GOLD_DARK}33`
      }}>
          <Icon className="h-[18px] w-[18px]" strokeWidth={2} style={{
          color: GOLD_DARK
        } as React.CSSProperties} />
        </div>
        <span className="text-[10px] font-extrabold tracking-[0.18em]" style={{
        color: `${GOLD_DARK}99`,
        fontFamily: FONT_HEAD,
        fontVariantNumeric: "tabular-nums"
      }}>
          {num}
        </span>
      </div>

      {/* Content */}
      <div className="flex-1 min-w-0 pt-0.5">
        <h3 className="font-bold mb-1.5" style={{
        fontFamily: FONT_HEAD,
        color: NAVY,
        fontSize: "1.0625rem",
        lineHeight: 1.25,
        letterSpacing: "-0.012em"
      }}>
          {pledge.title}
        </h3>
        <p className="text-[14px] leading-[1.65]" style={{
        color: INK_MUTED
      }}>
          {pledge.body}
        </p>
      </div>
    </li>;
}